import { useEffect, useState } from 'react';
import { MainTitle } from 'components/MainTitle';
import { Nothing } from 'components/Nothing';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import styles from './Users.module.scss';
import { InputSearch } from 'ui/InputSearch';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useGetUsersQuery } from 'redux/Api/usersApi/usersApi';
import { UsersList } from 'components/UsersList';
import { UserType } from 'redux/Api/usersApi/type';

interface QueryParams {
  page: number;
  per_page: number;
  search?: string;
}

export const Users = () => {
  const [searchValue, setSearchValue] = useState('');
  const [allItems, setAllItems] = useState<UserType[]>([]);

  const per_page = 50;

  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    per_page,
  });

  const { data, isLoading } = useGetUsersQuery(queryParams);

  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      if (queryParams.page === 1) {
        setAllItems(data.data);
      } else {
        setAllItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = data.data.filter(
            (elem: UserType) => !existingIds.has(elem.id),
          );
          return [...prev, ...newItems];
        });
      }
    } else if (data) {
      setAllItems([]);
    }
  }, [data, queryParams.page]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: searchValue || undefined,
      }));
      setAllItems([]);
    }
  };

  const hasNextPage = data?.meta
    ? data?.meta?.last_page !== data?.meta?.current_page
    : false;

  const [intersectionRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setQueryParams((prev) => ({ ...prev, page: prev.page + 1 })),
    rootMargin: '0px 0px 800px 0px',
  });

  return (
    <div className={styles.root}>
      <MainTitle title='Пользователи' count={data?.meta.total} />
      <div className={styles.root_search}>
        <div className={styles.search_input}>
          <InputSearch
            notFocus
            placeholder='Искать'
            value={searchValue}
            onKeyDown={handleKeyDown}
            onSearch={() => {
              setQueryParams((prev) => ({
                ...prev,
                page: 1,
                search: searchValue || undefined,
              }));
              setAllItems([]);
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchValue(e.target.value);

              if (e.target.value === '') {
                setQueryParams((prev) => ({
                  ...prev,
                  page: 1,
                  search: undefined,
                }));
                setAllItems([]);
              }
            }}
          />
        </div>
      </div>
      {isLoading ? (
        <LoadingSkeleton mt />
      ) : allItems?.length ? (
        <UsersList usersList={allItems} intersectionRef={intersectionRef} />
      ) : (
        <Nothing style={{ height: '750px' }} title='Ничего не найдено' />
      )}
    </div>
  );
};
